/**
 * A startup's detail page (F3).
 *
 * Tabbed on wide screens; the tab strip scrolls horizontally below 768px
 * rather than wrapping (plan §7.3).
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 6.5
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { ActivityTab } from '../../components/domain/activity/ActivityTab';
import { BandBadge } from '../../components/domain/BandBadge';
import { DefinitionList } from '../../components/domain/DefinitionList';
import { Money } from '../../components/domain/Money';
import { StageBadge } from '../../components/domain/StageBadge';
import { Badge } from '../../components/ui/Badge';
import { Card, CardBody, CardHead } from '../../components/ui/Card';
import { Empty } from '../../components/ui/Empty';
import { Grid, Stack } from '../../components/ui/Grid';
import { Kicker } from '../../components/ui/Kicker';
import { PageHead } from '../../components/ui/PageHead';
import { TabPanel } from '../../components/ui/tab-panel';
import { Tabs } from '../../components/ui/Tabs';
import { formatDate, formatNumber } from '../../lib/format';
import { ROUTES } from '../../lib/routes';

import { useStartup } from './api/startups.queries';
import { ExitCriteriaChecklist } from './components/ExitCriteriaChecklist';
import { ScorecardTab } from './components/ScorecardTab';
import { StartupDocuments } from './components/StartupDocuments';
import { StartupLegalFinance } from './components/StartupLegalFinance';

export function StartupDetailPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const [tab, setTab] = useState('overview');

  const { data, isPending, isError } = useStartup(id);

  if (isPending) {
    return (
      <p role="status" className="text-body">
        {t('state.loading')}
      </p>
    );
  }

  /* `data` is already narrowed to defined by the isPending guard above, so
     isError is the only remaining condition. */
  if (isError) {
    return (
      <div>
        <p role="alert" className="text-sem-bad mb-3">
          {t('startup.notFound')}
        </p>
        <Link to={ROUTES.startups} className="text-blue text-sm underline">
          {t('startup.backToList')}
        </Link>
      </div>
    );
  }

  const { startup, checklist } = data;

  const tabs = [
    { value: 'overview', label: t('startup.tabs.overview') },
    { value: 'scorecard', label: t('startup.tabs.scorecard') },
    { value: 'activity', label: t('startup.tabs.activity') },
    { value: 'team', label: t('startup.tabs.team') },
    { value: 'documents', label: t('startup.tabs.documents') },
    { value: 'legal', label: t('startup.tabs.legal') },
  ];

  const notSet = <span className="text-gray-400">{t('startup.notSet')}</span>;

  return (
    <>
      {/* A breadcrumb, not a back link: it says where this page sits as well as
          how to leave it, which matters on a page reachable from the board, the
          table and a search result alike. */}
      <nav aria-label={t('startup.breadcrumb')} className="text-body mb-s2 text-xs">
        {/* Persistently underlined, not only on hover: inside a text block a
            link distinguished by colour alone fails WCAG 1.4.1 (PRD §9.7). */}
        <Link to={ROUTES.startups} className="text-blue underline">
          {t('startups.title')}
        </Link>
        <span className="text-gray-200 mx-1.5">/</span>
        <span>{startup.name}</span>
      </nav>

      {/* The badges sit in the actions slot, not inside the `h1`. The
          prototype puts the band beside the name, but a badge inside the
          heading joins its accessible name — "Band B GridFlex Analytics" — so
          heading navigation announces a label the reader did not ask for, and
          the page title no longer matches the record's name. */}
      <PageHead
        title={startup.name}
        actions={
          <>
            <BandBadge band={startup.priorityBand} />
            <StageBadge stage={startup.stage} />
            {startup.isStalled ? <Badge tone="warn">{t('startup.stalled')}</Badge> : null}
          </>
        }
      />

      <Tabs
        tabs={tabs}
        value={tab}
        onValueChange={setTab}
        ariaLabel={t('startup.tabs.overview')}
      >
        <TabPanel value="overview" className="pt-s3">
          {/* The prototype's asymmetric split: the profile is the substance,
              the ownership and gate cards are the margin notes beside it. */}
          <Grid cols="detail">
            <Stack>
              <Card>
                <CardHead>
                  <Kicker as="h2">{t('startup.sections.profile')}</Kicker>
                </CardHead>
                <CardBody>
                  <DefinitionList
                    items={[
                      {
                        label: t('startup.fields.website'),
                        value:
                          startup.website === null ? (
                            notSet
                          ) : (
                            /* `noreferrer` with `noopener`: an external site
                               opened from here should learn nothing about where
                               it came from. */
                            <a
                              href={startup.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue hover:underline"
                            >
                              {startup.website}
                            </a>
                          ),
                      },
                      {
                        label: t('startup.fields.country'),
                        value: startup.hqCountry ?? notSet,
                      },
                      {
                        label: t('startup.fields.trl'),
                        value:
                          startup.trl === null ? (
                            notSet
                          ) : (
                            /* "7 of 9" rather than a bare 7: the scale is not
                               something a reader should have to know, and the
                               separator is translated because "of" is not
                               punctuation. */
                            <span className="tabular-nums">
                              {t('startup.trlOfNine', {
                                value: formatNumber(startup.trl),
                              })}
                            </span>
                          ),
                      },
                      {
                        label: t('startup.fields.raised'),
                        value:
                          startup.totalRaisedEur === null ? (
                            notSet
                          ) : (
                            <Money value={startup.totalRaisedEur} style="exact" />
                          ),
                      },
                    ]}
                  />
                </CardBody>
              </Card>
            </Stack>

            <Stack>
              <Card>
                <CardHead>
                  <Kicker as="h2">{t('startup.sections.ownership')}</Kicker>
                </CardHead>
                <CardBody>
                  <DefinitionList
                    items={[
                      {
                        label: t('startup.fields.daysInStage'),
                        value: (
                          <span className="tabular-nums">
                            {formatNumber(startup.daysInStage)}
                          </span>
                        ),
                      },
                      {
                        label: t('startup.fields.nextAction'),
                        value: startup.nextAction ?? notSet,
                      },
                      {
                        label: t('startup.fields.nextActionDue'),
                        value:
                          startup.nextActionDueDate === null
                            ? notSet
                            : formatDate(startup.nextActionDueDate),
                      },
                    ]}
                  />
                </CardBody>
              </Card>

              {checklist.length > 0 && (
                <Card>
                  <CardHead>
                    <Kicker as="h2">
                      {t('startup.checklist.gateTo', {
                        stage: t(`stage.${startup.stage}`),
                      })}
                    </Kicker>
                  </CardHead>
                  <CardBody>
                    <ExitCriteriaChecklist items={checklist} />
                  </CardBody>
                </Card>
              )}
            </Stack>
          </Grid>
        </TabPanel>

        <TabPanel value="scorecard" className="pt-s3">
          {/* Mounted only when the tab is open, so its extra query is not made
              on every detail-page visit. */}
          {tab === 'scorecard' ? <ScorecardTab startupId={id} /> : null}
        </TabPanel>

        <TabPanel value="activity" className="pt-s3">
          {/* Mounted only when the tab is open, so its query is not made on
              every detail-page visit. */}
          {tab === 'activity' ? <ActivityTab startupId={id} /> : null}
        </TabPanel>

        <TabPanel value="documents" className="pt-s3">
          <StartupDocuments startupId={id} />
        </TabPanel>

        <TabPanel value="legal" className="pt-s3">
          <StartupLegalFinance startupId={id} />
        </TabPanel>

        {/* Team lands with the contact directory's per-startup view. */}
        {['team'].map((value) => (
          <TabPanel key={value} value={value} className="pt-s3">
            <Card>
              <Empty title={t('page.comingSoon')}>
                {t(`startup.tabs.pending.${value}`)}
              </Empty>
            </Card>
          </TabPanel>
        ))}
      </Tabs>
    </>
  );
}
